import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { ArrowLeft, HandHeart, Trash2, Palette, Send, Pencil, X } from "lucide-react";
import { toast } from "sonner";
import { getThemesForScope, getPostTheme, THEMED_MAX_CHARS } from "@/lib/postThemes";
import { ShareCardDialog } from "@/components/ShareCardDialog";

const GROUP_THEMES = getThemesForScope("group");

function EditGroupDialog({ open, group, onClose, onSaved }) {
  const [name, setName] = useState("");
  const [desc, setDesc] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open && group) { setName(group.name || ""); setDesc(group.description || ""); }
  }, [open, group]);

  if (!open || !group) return null;

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim()) { toast.error("Name is required."); return; }
    setBusy(true);
    try {
      const { data } = await api.patch(`/groups/${group.id}`, { name, description: desc });
      onSaved(data);
      toast.success("Group updated");
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not update.");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-jjblue/60 backdrop-blur-sm" data-testid="group-edit-dialog">
      <div className="jj-card w-full max-w-lg p-7 relative">
        <button onClick={onClose} className="absolute top-4 right-4 text-[var(--jj-muted)] hover:text-burgundy"><X className="h-5 w-5" /></button>
        <div className="overline text-burgundy">Edit prayer group</div>
        <h2 className="font-serif text-2xl text-jjblue mt-2 leading-tight">Update the details</h2>
        <form onSubmit={submit} className="mt-5 space-y-3">
          <input className="input-jj" placeholder="Group name" value={name} onChange={(e) => setName(e.target.value)} data-testid="group-detail-edit-name" />
          <textarea rows={4} maxLength={500} className="input-jj" placeholder="Description" value={desc} onChange={(e) => setDesc(e.target.value)} data-testid="group-detail-edit-desc" />
          <div className="flex items-center justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="btn-secondary !py-2 !px-4 text-sm">Cancel</button>
            <button disabled={busy} className="btn-primary !py-2 !px-5 text-sm" data-testid="group-detail-edit-submit">{busy ? "Saving…" : "Save changes"}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function PrayerCard({ p, user, onPray, onDelete }) {
  const [shareOpen, setShareOpen] = useState(false);
  const canDel = user?.id === p.author?.id || user?.role === "admin";
  const themeId = p.background && p.background !== "none" ? p.background : null;
  const showThemedHero = !!themeId && (p.content?.length || 0) <= THEMED_MAX_CHARS;
  const theme = getPostTheme(themeId);

  if (showThemedHero) {
    return (
      <div
        className="rounded-2xl p-6 sm:p-7 relative overflow-hidden fade-up"
        style={{ background: theme.background, color: theme.text }}
        data-testid={`prayer-${p.id}`}
      >
        <div className="flex items-center justify-between text-xs opacity-80" style={{ color: theme.text }}>
          <span>{p.author?.name}</span>
          <div className="flex items-center gap-3">
            <span>{new Date(p.created_at).toLocaleString()}</span>
            {canDel && (
              <button onClick={() => onDelete(p)} aria-label="Delete prayer"
                className="opacity-80 hover:opacity-100 transition-opacity" data-testid={`prayer-delete-${p.id}`}
                style={{ color: theme.text }}>
                <Trash2 className="h-3.5 w-3.5" strokeWidth={1.5} />
              </button>
            )}
          </div>
        </div>
        <p className="mt-4 font-serif text-xl sm:text-2xl leading-snug whitespace-pre-wrap">{p.content}</p>
        <div className="mt-5 flex items-center justify-between gap-2 flex-wrap">
          <button
            onClick={() => onPray(p)}
            className="inline-flex items-center gap-1.5 text-sm rounded-full px-3 py-1.5 transition-colors"
            style={{ background: p.i_am_praying ? theme.accent : "rgba(255,255,255,0.16)", color: theme.text }}
            data-testid={`pray-${p.id}`}
          >
            <HandHeart className="h-4 w-4" strokeWidth={1.5} /> {p.i_am_praying ? "Interceding" : "I'm praying"} · {p.praying_count}
          </button>
          <button
            onClick={() => setShareOpen(true)}
            className="inline-flex items-center gap-1.5 text-sm rounded-full px-3 py-1.5 transition-colors"
            style={{ background: "rgba(255,255,255,0.16)", color: theme.text }}
            data-testid={`prayer-send-image-${p.id}`}
          >
            <Send className="h-4 w-4" strokeWidth={1.5} /> Send
          </button>
        </div>
        <ShareCardDialog
          open={shareOpen}
          onClose={() => setShareOpen(false)}
          content={p.content}
          background={p.background}
          authorName={p.author?.name}
          kind="prayer"
        />
      </div>
    );
  }

  return (
    <div className="jj-card p-6" data-testid={`prayer-${p.id}`}>
      <div className="flex items-center justify-between text-xs text-[var(--jj-muted)]">
        <span>{p.author?.name}</span>
        <div className="flex items-center gap-3">
          <span>{new Date(p.created_at).toLocaleString()}</span>
          {canDel && (
            <button onClick={() => onDelete(p)} aria-label="Delete prayer"
              className="hover:text-burgundy transition-colors" data-testid={`prayer-delete-${p.id}`}>
              <Trash2 className="h-3.5 w-3.5" strokeWidth={1.5} />
            </button>
          )}
        </div>
      </div>
      <p className="mt-3 text-jjblue whitespace-pre-wrap leading-relaxed">{p.content}</p>
      <button onClick={() => onPray(p)} className={`mt-5 inline-flex items-center gap-1.5 text-sm ${p.i_am_praying ? "text-burgundy" : "text-jjblue/80"} hover:text-burgundy transition-colors`} data-testid={`pray-${p.id}`}>
        <HandHeart className="h-4 w-4" strokeWidth={1.5} /> {p.i_am_praying ? "Interceding" : "I'm praying"} · {p.praying_count}
      </button>
    </div>
  );
}

export default function GroupDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [group, setGroup] = useState(null);
  const [prayers, setPrayers] = useState([]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [background, setBackground] = useState("none");
  const [themeOpen, setThemeOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);

  const load = async () => {
    const [g, p] = await Promise.all([
      api.get(`/groups/${id}`).then((r) => r.data),
      api.get(`/groups/${id}/prayers`).then((r) => r.data),
    ]);
    setGroup(g); setPrayers(p);
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [id]);

  const activeTheme = getPostTheme(background);
  const themedPreview = background !== "none" && text.length > 0 && text.length <= THEMED_MAX_CHARS;

  const submit = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    setBusy(true);
    try {
      await api.post(`/groups/${id}/prayers`, { content: text, background });
      setText(""); setBackground("none"); setThemeOpen(false);
      load();
      toast.success("Prayer request shared");
    } finally { setBusy(false); }
  };

  const pray = async (p) => {
    const { data } = await api.post(`/prayers/${p.id}/pray`);
    setPrayers(prayers.map((x) => x.id === p.id ? { ...x, ...data } : x));
  };

  const removePrayer = async (p) => {
    if (!window.confirm("Delete this prayer request?")) return;
    try {
      await api.delete(`/prayers/${p.id}`);
      setPrayers(prayers.filter((x) => x.id !== p.id));
      toast.success("Prayer request deleted");
    } catch {
      toast.error("Could not delete");
    }
  };

  const join = async () => {
    await api.post(`/groups/${id}/${group.is_member ? "leave" : "join"}`);
    load();
  };

  const removeGroup = async () => {
    if (!window.confirm(`Delete "${group.name}"? This will also remove its prayer requests.`)) return;
    try {
      await api.delete(`/groups/${id}`);
      toast.success("Group deleted");
      navigate("/groups");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not delete.");
    }
  };

  if (!group) return <div className="text-center py-20 text-[var(--jj-muted)]">Loading…</div>;

  const canManage = user && (user.role === "admin" || group.owner_id === user.id);

  return (
    <div className="max-w-3xl mx-auto px-6 py-10">
      <Link to="/groups" className="inline-flex items-center text-sm text-burgundy gap-1" data-testid="group-back">
        <ArrowLeft className="h-4 w-4" /> All groups
      </Link>
      <div className="mt-5 jj-card p-8" data-testid="group-header">
        <div className="flex items-start justify-between gap-3">
          <div className="overline text-burgundy">Prayer Group</div>
          {canManage && (
            <div className="flex items-center gap-1">
              <button onClick={() => setEditOpen(true)} className="p-1.5 text-[var(--jj-muted)] hover:text-burgundy" title="Edit group" data-testid="group-detail-edit">
                <Pencil className="h-3.5 w-3.5" />
              </button>
              <button onClick={removeGroup} className="p-1.5 text-[var(--jj-muted)] hover:text-burgundy" title="Delete group" data-testid="group-detail-delete">
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </div>
          )}
        </div>
        <h1 className="font-serif text-4xl text-jjblue mt-2">{group.name}</h1>
        <p className="mt-3 text-[var(--jj-text-secondary)]">{group.description}</p>
        <div className="mt-5 flex items-center justify-between">
          <div className="text-sm text-[var(--jj-muted)]">{group.member_count} interceding</div>
          <button onClick={join} className={group.is_member ? "btn-secondary !py-2 !px-4 text-sm" : "btn-primary !py-2 !px-4 text-sm"} data-testid="group-detail-toggle">
            {group.is_member ? "Leave group" : "Join group"}
          </button>
        </div>
      </div>

      {group.is_member && (
        <form onSubmit={submit} className="jj-card p-6 mt-6" data-testid="prayer-composer">
          <label className="text-sm text-jjblue/80">Share a prayer request</label>
          {themedPreview ? (
            <div
              className="mt-2 rounded-2xl px-6 py-8 relative overflow-hidden transition-colors duration-300"
              style={{ background: activeTheme.background, color: activeTheme.text }}
              data-testid="prayer-themed-preview"
            >
              <textarea
                rows={3}
                maxLength={THEMED_MAX_CHARS}
                placeholder="Lift it here, the family will pray."
                value={text}
                onChange={(e) => setText(e.target.value)}
                className="w-full bg-transparent border-none focus:outline-none resize-none font-serif text-xl sm:text-2xl leading-snug placeholder:opacity-60"
                style={{ color: activeTheme.text }}
                data-testid="prayer-input"
              />
              <div className="mt-2 text-xs opacity-70" style={{ color: activeTheme.text }}>
                {text.length}/{THEMED_MAX_CHARS} · themed prayers stay short
              </div>
            </div>
          ) : (
            <textarea rows={3} className="input-jj mt-2" value={text} onChange={(e) => setText(e.target.value)}
              placeholder="Lift it here, the family will pray." data-testid="prayer-input" />
          )}
          {themeOpen && (
            <div className="mt-3 flex flex-wrap gap-2 items-center" data-testid="prayer-theme-picker">
              <span className="text-xs text-[var(--jj-muted)] mr-1">Background:</span>
              {GROUP_THEMES.map((t) => (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => setBackground(t.id)}
                  title={t.label}
                  aria-label={`Background ${t.label}`}
                  className={`h-9 w-9 rounded-full border-2 transition ${background === t.id ? "border-[var(--jj-blue)] scale-110" : "border-white hover:scale-105"} shadow-sm`}
                  style={{ background: t.swatch }}
                  data-testid={`prayer-theme-${t.id}`}
                />
              ))}
            </div>
          )}
          <div className="mt-3 flex justify-between items-center gap-2">
            <button type="button" onClick={() => setThemeOpen((v) => !v)}
              className={`btn-secondary !py-2 !px-4 text-sm flex items-center gap-2 ${themeOpen ? "!border-[var(--jj-gold)] !bg-white" : ""}`}
              data-testid="prayer-theme-btn">
              <Palette className="h-4 w-4" strokeWidth={1.5} /> Background
              {background !== "none" && (
                <span
                  className="ml-1 h-3 w-3 rounded-full border border-white shadow"
                  style={{ background: activeTheme.swatch }}
                  aria-hidden
                />
              )}
            </button>
            <button disabled={busy} className="btn-primary !py-2 !px-4 text-sm" data-testid="prayer-submit">{busy ? "Sharing…" : "Share request"}</button>
          </div>
        </form>
      )}

      <div className="mt-8 space-y-4">
        {prayers.length === 0 && <div className="text-center py-10 text-[var(--jj-muted)]">No prayer requests yet.</div>}
        {prayers.map((p) => (
          <PrayerCard key={p.id} p={p} user={user} onPray={pray} onDelete={removePrayer} />
        ))}
      </div>
      <EditGroupDialog
        open={editOpen}
        group={group}
        onClose={() => setEditOpen(false)}
        onSaved={(updated) => setGroup((prev) => ({ ...prev, ...updated }))}
      />
    </div>
  );
}
